import { HttpClient } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { DomSanitizer } from '@angular/platform-browser';
import { CacheExpiryConstants, CacheKeyConstants, CacheService, isNullOrUndefined } from '@uzurv/core';
// eslint-disable-next-line import/order
import { UzurvRestConfiguration, UzurvRestService, UZURV_REST_CONFIGURATION } from '@uzurv/http';
import { Deserialize, Serialize } from 'cerialize';
import { Observable, of } from 'rxjs';
import { map } from 'rxjs/operators';
import { VehicleType, VehicleVehicleType } from '../../models';

// TODO: This whole thing might be deprecated with dots. Do we need these vehicle types anymore?
@Injectable({
  providedIn: 'root'
})
export class VehicleService extends UzurvRestService {
  readonly resourceUrl = 'vehicles/';

  constructor(
    httpClient: HttpClient,
    @Inject(UZURV_REST_CONFIGURATION)
    protected uzurvRestConfiguration: UzurvRestConfiguration,
    domSanitizer: DomSanitizer,
    private cacheService: CacheService
  ) {
    super(httpClient, uzurvRestConfiguration, domSanitizer);
  }

  /**
   * Returns a list of vehicle types
   *
   * @param overrideCache
   */
  getVehicleTypes(overrideCache = false): Observable<Array<VehicleType>> {
    const cachedData = this.cacheService.getCachedData(CacheKeyConstants.VEHICLE_TYPES);
    if (!overrideCache && !isNullOrUndefined(cachedData)) {
      return of(Deserialize(JSON.parse(cachedData), VehicleType));
    }

    return this.get<Array<VehicleType>>(`${this.resourceUrl}vehicleTypes/`, {
      pageNumber: 0,
      pageSize: 100
    }).pipe(map(response => {
      this.cacheService.cacheData(CacheKeyConstants.VEHICLE_TYPES, JSON.stringify(response), CacheExpiryConstants.DOMAIN_CACHE_EXPIRY);
      return Deserialize(response, VehicleType);
    }));
  }

  /**
   * Returns a vehicle type by ID
   *
   * @param id
   * @param overrideCache
   */
  findVehicleTypeById(id: number, overrideCache = false): Observable<VehicleType> {
    return this.getVehicleTypes(overrideCache).pipe(map(response => response.find(vehicleType => vehicleType.id === id)));
  }

  /**
   * Returns a vehicle type by name
   *
   * @param name
   * @param overrideCache
   */
  findVehicleTypeByName(name: string, overrideCache = false): Observable<VehicleType> {
    return this.getVehicleTypes(overrideCache).pipe(map(response => response.find(vehicleType => vehicleType.name === name)));
  }

  /**
   * Returns a list of vehicle types for a vehicle
   *
   * @param vehicleId
   * @deprecated
   */
  getVehicleTypesForVehicle(vehicleId: number): Observable<Array<VehicleVehicleType>> {
    return this.get<Array<VehicleVehicleType>>(`${this.resourceUrl}${vehicleId}/vehicleTypes/`).pipe(map(response =>
      Deserialize(response, VehicleVehicleType)
    ));
  }

  /**
   * Updates a vehicle type with active status
   *
   * @param vehicleVehicleType
   * @param active
   */
  updateVehicleVehicleTypeWithActive(vehicleVehicleType: VehicleVehicleType, active: boolean): Observable<VehicleVehicleType> {
    return this.put(
      `${this.resourceUrl}${vehicleVehicleType.vehicleId}/vehicleTypes/${vehicleVehicleType.vehicleType.id}?isActive=${active}`,
      Serialize(vehicleVehicleType.vehicleType)
    ).pipe(map(response => Deserialize(response, VehicleVehicleType)));
  }
}
